import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { X, ArrowRight, Scale, Plus } from 'lucide-react';
import { Button } from './ui/button';
import { useCompare } from '../contexts/CompareContext';
import { slugsToProducts } from '../data/compareUtils';

const CompareDrawer = () => {
  const { slugs, remove, clear, max } = useCompare();
  const location = useLocation();
  const products = slugsToProducts(slugs);

  // Hide on the compare page itself (avoid redundancy + table overlap)
  if (location.pathname === '/compare') return null;
  if (products.length === 0) return null;

  return (
    <div
      className="fixed bottom-0 left-0 right-0 z-50 bg-white border-t-2 border-[#B5D43B] shadow-2xl"
      data-testid="compare-drawer"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 pr-20 sm:pr-24">
        <div className="flex items-center gap-4 flex-wrap">
          <div className="flex items-center gap-2 flex-shrink-0">
            <div className="w-9 h-9 rounded-lg bg-[#3D5A47] flex items-center justify-center">
              <Scale className="w-5 h-5 text-[#B5D43B]" />
            </div>
            <div>
              <p className="text-xs text-gray-500 leading-tight">Compare</p>
              <p className="font-bold text-sm text-[#3D5A47] leading-tight">
                {products.length} of {max}
              </p>
            </div>
          </div>

          {/* Selected thumbnails */}
          <div className="flex items-center gap-2 flex-1 min-w-0 overflow-x-auto">
            {products.map((p) => (
              <div
                key={p.slug}
                className="flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-xl pl-2 pr-1 py-1 flex-shrink-0"
                data-testid={`compare-chip-${p.slug}`}
              >
                <img
                  src={p.image}
                  alt={p.name}
                  className="w-9 h-9 object-contain rounded bg-white"
                />
                <div className="hidden sm:block max-w-[180px] truncate">
                  <p className="text-[11px] text-gray-400 font-mono truncate">{p.model}</p>
                  <p className="text-xs font-semibold text-[#3D5A47] truncate">{p.name.split(' (')[0]}</p>
                </div>
                <button
                  onClick={() => remove(p.slug)}
                  className="w-6 h-6 rounded hover:bg-gray-200 flex items-center justify-center text-gray-500 hover:text-[#3D5A47]"
                  aria-label={`Remove ${p.name} from comparison`}
                  data-testid={`compare-remove-${p.slug}`}
                >
                  <X className="w-3.5 h-3.5 pointer-events-none" />
                </button>
              </div>
            ))}
            {products.length < max && (
              <div className="flex-shrink-0 text-xs text-gray-400 px-2 inline-flex items-center gap-1">
                <Plus className="w-3 h-3" /> Add up to {max - products.length} more
              </div>
            )}
          </div>

          <div className="flex items-center gap-2 flex-shrink-0">
            <button
              onClick={clear}
              className="text-xs text-gray-500 hover:text-[#3D5A47] px-2 py-1"
              data-testid="compare-clear"
            >
              Clear
            </button>
            <Link to="/compare">
              <Button
                className="bg-[#B5D43B] hover:bg-[#a3c235] text-[#3D5A47] gap-2 font-semibold"
                disabled={products.length < 2}
                data-testid="compare-go"
              >
                Compare Now <ArrowRight className="w-4 h-4" />
              </Button>
            </Link>
          </div>
        </div>
        {products.length < 2 && (
          <p className="text-[11px] text-gray-500 mt-1">
            Select at least 2 models to compare.
          </p>
        )}
      </div>
    </div>
  );
};

export default CompareDrawer;
